import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Editor as TipTapEditor } from '@tiptap/react'
import { Resizable } from 're-resizable'
import debounce from 'lodash.debounce'
import max from 'lodash.max'

import { cn } from '@/utils'
import { Editor } from '@/components/editor/Editor'
import { Spinner } from '@/components/ui/spinner'
import { useEditDocPageNote } from '@/services/queries/document_notes/useEditDocPageNote'
import { useDocumentState } from '@/state/document'
import {
  Note,
  TemplateAttribute,
  useListNotes,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'

import { useAnnotationLayer } from '../Annotations/queries'
import {
  getAbsoluteXFromDocumentPageOrder,
  getAbsoluteYFromSubPage,
} from '../Annotations/utils'
import { DocumentPage } from '@/lib/api/client'
import { useAnnotationsContext } from '../Annotations/AnnotationsContext'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useParams } from 'wouter'

interface OperationNotesEditorProps {
  template?: TemplateAttribute<Note>
  isReadOnly: boolean
  documentPage: DocumentPage
  dataType: string
}

export const OperationNotesEditor = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
}: OperationNotesEditorProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const editorRef = useRef<TipTapEditor | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )

  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPageId)

  const notesTemplates = useListNotes(documentPageId)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplateElement({
      documentPageId,
    })
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template?.id,
    [selectedElement, documentPageId, template?.id],
  )
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const size = useMemo(() => template?.template_values?.size, [template])

  const notesPosition = useMemo(
    () => template?.template_values?.notesPosition,
    [template],
  )
  const documentPageOrder = useMemo(
    () => notesTemplates?.findIndex((note) => note.id === template?.id),
    [notesTemplates, template],
  )
  const isAbsolute = useMemo(
    () => notesPosition === 'absolute',
    [notesPosition],
  )

  const updateNotesSizeHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position,
        },
        data_type: dataType as any,
        templateAttributeId: template?.id as string,
      })
    },
    [dataType, template?.id, updateTemplate],
  )

  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [reset, setReset] = useState({
    width: 0,
    height: 0,
    position: {
      x: 0,
      y: 0,
    },
    delta: { x: 0, y: 0 },
  })

  useEffect(() => {
    let resizeObserver: ResizeObserver
    const minDimensions = { width: 300, height: 100 }
    if (containerRef.current && !size) {
      const rect = containerRef.current.getBoundingClientRect()
      const width = max([rect.width, minDimensions.width])
      const height = max([rect.height, minDimensions.height])
      setContainerDimensions({
        width,
        height,
      })

      resizeObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect

        setContainerDimensions({
          width: max([width, minDimensions.width]),
          height: max([height, minDimensions.height]),
        })
      })

      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [containerRef, size])

  useEffect(() => {
    if (size) {
      setContainerDimensions(size)
    }
  }, [size])

  const {
    isOutOfBounds,
    setIsOutOfBounds,
    setIsResizing,
    annotationsCanvasRef,
  } = useAnnotationsContext()
  const [{ position, delta }, setPosition] = useState({
    position: positionData,
    delta: INITIAL_DELTA,
  })
  const { mutate: updateNotes } = useEditDocPageNote({
    documentPageId,
    notesId: template?.id as string,
    useOptimisticUpdate: true,
  })
  const containerId = useMemo(
    () => `${documentPageId}-${template?.id}`,
    [documentPageId, template?.id],
  )

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useDraggable({
      disabled: isAddingElement || isReadOnly,
      id: template?.id as string,
      data: { type: dataType },
    })

  useEffect(() => {
    if (!(transform?.x || transform?.y)) return
    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (transform?.x || 0) / zoomRatio,
        y: (transform?.y || 0) / zoomRatio,
      },
    }))
  }, [transform, zoomRatio])

  useEffect(() => {
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const saveNotes = useCallback(
    debounce(({ editor }: { editor: TipTapEditor }) => {
      updateNotes({
        editor,
        notesPosition: notesPosition as 'absolute' | 'relative',
      })
    }, 500),
    [updateNotes],
  )

  const adjustEditorHeight = useCallback(
    ({
      editor,
      size,
    }: {
      editor: TipTapEditor
      size: { height: number; width: number }
    }) => {
      const scrollHeight = editor.options.element?.scrollHeight || 0
      const containerHeight = size.height
      const height = Math.max(scrollHeight, containerHeight)
      const updatedSize = {
        height,
        width: size.width,
      }
      setContainerDimensions(updatedSize)
      return updatedSize
    },
    [],
  )

  const mutatedListeners = useMemo(
    () => ({
      ...listeners,
      onMouseDown: (e: React.MouseEvent<Element, MouseEvent>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
      onTouchStart: (e: React.TouchEvent<Element>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
    }),
    [listeners, containerId],
  )

  const isLoading = useMemo(() => isUpdatingTemplate, [isUpdatingTemplate])
  const content = useMemo(() => template?.template_values?.content || '', [])

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              action: () => bringToFront(template?.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(template?.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(template?.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(template?.id as string),
            },
          ],
        },
      ]}
    >
      <div
        className="flex flex-1 p-px absolute top-0 left-0"
        ref={(el) => {
          if (template?.id) {
            setDraggableAnnotationParentRef(template.id, el)
          }
        }}
      >
        <div
          className="relative flex flex-1 h-full w-full"
          ref={containerRef}
        />
        <div
          id={containerId}
          ref={setNodeRef}
          className={cn('border-4 flex rounded-md box-content absolute', {
            'cursor-move': !isReadOnly,
            'cursor-default': isReadOnly,
            'rounded-md': isSelected,
            'shadow-md': isDragging,
            'shadow-gray-200': isDragging,
            'bg-sky-200/10': isDragging,
            'pointer-events-none': isAddingElement,
            'pointer-events-auto': !isAddingElement,
            'z-[100]': isDragging,
          })}
          {...attributes}
          {...mutatedListeners}
          style={{
            translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
            top: getAbsoluteYFromSubPage({
              subPageId,
              relativeY: position?.y || 0,
              subPageIds: documentSubPages,
              isRelative: !isAbsolute,
              relativeOffset: 350,
            }),
            left: getAbsoluteXFromDocumentPageOrder({
              relativeX: position?.x || 0,
              isRelative: !isAbsolute,
              documentPageOrder,
              relativeOffset: 350,
            }),
            zIndex: position?.z || 1000,
            borderColor: isSelected
              ? 'rgba(129, 140, 248, 0.5)'
              : 'transparent',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Resizable
            minWidth={150}
            minHeight={45}
            maxWidth={740}
            size={
              containerDimensions.width > 0 && containerDimensions.height > 0
                ? containerDimensions
                : undefined
            }
            onResizeStart={(event, _, ref) => {
              event.stopPropagation()
              const width = ref.clientWidth
              const height = ref.clientHeight
              if (setIsResizing) setIsResizing(true)
              setContainerDimensions({ width, height })
              setReset({ width, height, position, delta } as any)
            }}
            onResize={(event, direction, ref) => {
              event.stopPropagation()
              const canvasRect =
                annotationsCanvasRef?.current?.getBoundingClientRect()
              const elementRect = ref.getBoundingClientRect()

              if (!canvasRect) return

              if (
                elementRect.right > canvasRect?.right ||
                elementRect.top < canvasRect?.top ||
                elementRect.left < canvasRect?.left ||
                elementRect.bottom > canvasRect?.bottom
              ) {
                setIsOutOfBounds(true)
                return
              } else if (isOutOfBounds) {
                setIsOutOfBounds(false)
              }

              const newWidth = ref.clientWidth
              const newHeight = ref.clientHeight
              const widthDiff = newWidth - containerDimensions.width
              const heightDiff = newHeight - containerDimensions.height

              const deltaUpdates = {
                topLeft: { x: -widthDiff, y: -heightDiff },
                bottomLeft: { x: -widthDiff, y: 0 },
                topRight: { x: 0, y: -heightDiff },
                bottomRight: { x: 0, y: 0 },
              }

              setContainerDimensions({
                width: newWidth,
                height: newHeight,
              })

              setPosition((prevPosition) => ({
                ...prevPosition,
                delta: {
                  x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                  y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
                },
              }))
            }}
            onResizeStop={(event) => {
              event.stopPropagation()
              if (setIsResizing) setIsResizing(false)
              if (!editorRef.current) return

              if (isOutOfBounds) {
                const { position, delta, width, height } = reset
                setContainerDimensions({ width, height })
                setPosition({ position, delta })
                setIsOutOfBounds(false)
                return
              }

              if (
                containerDimensions.width < 150 ||
                containerDimensions.height < 45
              )
                return

              updateNotesSizeHandler(
                {
                  width: containerDimensions.width,
                  height: containerDimensions.height,
                },
                {
                  x: (position?.x || 0) + delta.x,
                  y: (position?.y || 0) + delta.y,
                },
              )
                .then(() => {
                  selectElement({
                    documentPageId,
                    id: template?.id as string,
                    isRemovable: true,
                  })
                })
                .catch((error) => {
                  console.error('Error updating image size:', error)
                })
            }}
            handleClasses={{
              topRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              topLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
            }}
            enable={{
              topRight: true,
              topLeft: true,
              bottomRight: true,
              bottomLeft: true,
              top: false,
              right: false,
              bottom: false,
              left: false,
            }}
          >
            <>
              {isLoading && (
                <div className="absolute h-full w-full bg-zinc-400/60 z-10">
                  <Spinner />
                </div>
              )}
              <Editor
                projectId={projectId}
                documentId={documentId}
                documentPageId={documentPageId}
                className={cn('flex-1 bg-transparent overflow-auto', {
                  'pointer-events-none': isAddingElement || isDragging,
                })}
                onBlur={saveNotes}
                onClick={() => {
                  setSelectedElement({
                    id: template?.id as string,
                    documentPageId,
                    isRemovable: false,
                  })
                }}
                onUpdate={({ editor }: { editor: TipTapEditor }) => {
                  adjustEditorHeight({
                    editor,
                    size: containerDimensions,
                  })
                  saveNotes({ editor })
                }}
                content={content}
                disabled={isReadOnly}
                placeholder="Write something or press '/' for commands..."
                isDragging={isDragging}
                updateBomWithSlashCommands={true}
                isTransparent={true}
                editorRef={editorRef}
              />
            </>
          </Resizable>
        </div>
      </div>
    </OptionsContextMenu>
  )
}
